import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { App as AntApp, message } from "antd";
import FirstLoginPortal from "./components/firstloginportal";
import MaintenancePage from "./components/MaintenancePage";
import { getApiBase } from "./utils/getApiBase";
import { setUserPermissions } from "./components/admin/utils/rolePermissions";
import './App.css';

const AdminDashboard = lazy(() => import("./components/main"));
const SponsorsPage = lazy(() => import("./ads"));
const LoginPage = lazy(() => import("./components/login"));
const LandingPage = lazy(() => import("./components/paidLandingPage"));
const SuccessPage = lazy(() => import("./components/paidSuccess"));
const PaymentRedirect = lazy(() => import("./components/paymentRedirect"));
const VoucherLogin = lazy(() => import("./components/VoucherLogin"));
const LoginVoucher = lazy(() => import("./components/loginVoucher"));
const ResetPasswordPage = lazy(() => import("./components/ResetPassword"));
const Success = lazy(() => import("./components/success"));

const routeFallback = <div style={{ minHeight: "100vh" }} />;

  // react-toastify handles global notification UI

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [authChecked, setAuthChecked] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const API_BASE = getApiBase();

  // Check maintenance mode on app load
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/admin/maintenance`);
        if (response.data?.success && response.data?.data?.is_enabled) {
          setIsMaintenanceMode(true);
          setMaintenanceMessage(response.data.data.message || 'We are currently under maintenance. Please check back later.');
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      } finally {
        setMaintenanceChecked(true);
      }
    };
    
    checkMaintenanceMode();
  }, [API_BASE]);

  useEffect(() => {
    message.config({ maxCount: 1 });
  }, []);

  useEffect(() => {
    const title = localStorage.getItem('title');
    if (title) {
      setWelcomeTitle(title);
    } else {
      setWelcomeTitle('WELCOME');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        // No locally stored token, but the server may still hold a valid session
        // (e.g. we just arrived via a magic-link redirect, which carries the
        // session cookie but no JSON for the client to capture). Hydrate from it.
        try {
          const sess = await axios.get(`${API_BASE}/api/admin/session`, { withCredentials: true });
          if (sess.data?.success && sess.data?.user) {
            const u = sess.data.user;
            const userData = {
              username: u.userName,
              userName: u.userName,
              identifier: u.identifier,
              role: u.role,
              phoneNumber: u.phoneNumber,
              partnerType: u.partnerType || null,
              roles: u.roles || [],
              permissions: u.permissions || []
            };
            if (isMounted) {
              setUserPermissions(userData.permissions, userData.roles);
              setCurrentUser(userData);
              setIsAuthenticated(true);
            }
            localStorage.setItem('adminToken', JSON.stringify(userData));
          }
        } catch (_) {
          /* no active session — remain unauthenticated */
        } finally {
          if (isMounted) setAuthChecked(true);
        }
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/admin/me`, { withCredentials: true });
        if (response.data?.success && response.data?.profile) {
          const profile = response.data.profile;
          const stored = JSON.parse(token);
          const userData = {
            username: stored.username || profile.userName,
            userName: stored.userName || profile.userName,
            identifier: profile.identifier,
            role: profile.role,
            phoneNumber: profile.phoneNumber,
            partnerType: profile.partnerType || null,
            roles: stored.roles || [],
            permissions: stored.permissions || []
          };

          if (isMounted) {
            setUserPermissions(userData.permissions, userData.roles);
            setCurrentUser(userData);
            setIsAuthenticated(true);
          }

          localStorage.setItem('adminToken', JSON.stringify(userData));
        } else {
          localStorage.removeItem('adminToken');
        }
      } catch (_) {
        localStorage.removeItem('adminToken');
      } finally {
        if (isMounted) setAuthChecked(true);
      }
    };

    bootstrapAuth();
    return () => { isMounted = false; };
  }, [API_BASE]);
  
  // Show loading while checking maintenance mode
  if (!maintenanceChecked) {
    return (
      <AntApp>
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}>
          Loading...
        </div>
      </AntApp>
    );
  }

  // Show maintenance page if maintenance mode is enabled
  if (isMaintenanceMode) {
    return (
      <AntApp>
        <MaintenancePage message={maintenanceMessage} />
      </AntApp>
    );
  }
  
  return (
    <AntApp>
      <Router>
        <Routes>
          <Route path="/" element={<FirstLoginPortal API_BASE={API_BASE} welcomeTitle={welcomeTitle} />} />
          <Route
            path="/page/login"
            element={
              !authChecked
                ? routeFallback
                : isAuthenticated
                  ? <Navigate to="/page/main" />
                  : <Suspense fallback={routeFallback}><LoginPage setIsAuthenticated={setIsAuthenticated} API_BASE={API_BASE} currentUser={currentUser} setCurrentUser={setCurrentUser} /></Suspense>
            }
          />
          <Route path="/page/main" element={!authChecked ? routeFallback : isAuthenticated ? <Suspense fallback={routeFallback}><AdminDashboard setIsAuthenticated={setIsAuthenticated} API_BASE={API_BASE} currentUser={currentUser} setCurrentUser={setCurrentUser} /></Suspense> : <Navigate to="/page/login" /> } />
          <Route path="/page/success" element={<Suspense fallback={routeFallback}><Success API_BASE={API_BASE} /></Suspense>} />
          <Route path="/page/payment-success" element={<Suspense fallback={routeFallback}><SuccessPage API_BASE={API_BASE} /></Suspense>} />
          <Route path="/page/payment-redirect" element={<Suspense fallback={routeFallback}><PaymentRedirect /></Suspense>} />
          <Route path="/page/payment-voucher" element={<Suspense fallback={routeFallback}><LandingPage API_BASE={API_BASE} /></Suspense>} />
          <Route path="/page/voucher-login" element={<Suspense fallback={routeFallback}><VoucherLogin API_BASE={API_BASE} /></Suspense>} />
          <Route path="/page/login-voucher" element={<Suspense fallback={routeFallback}><LoginVoucher API_BASE={API_BASE} /></Suspense>} />
          <Route path="/page/reset-password/:token" element={<Suspense fallback={routeFallback}><ResetPasswordPage /></Suspense>} />
          <Route path="/page/ads" element={<Suspense fallback={routeFallback}><SponsorsPage API_BASE={API_BASE} /></Suspense>} />
          <Route path="/page/firstloginportal" element={<FirstLoginPortal API_BASE={API_BASE} welcomeTitle={welcomeTitle} />} />
          <Route path="/page/maintenance" element={<MaintenancePage />} />
          <Route path="/error" element={<div style={{ padding: '20px', textAlign: 'center' }}><h2>Payment Error</h2><p>There was an error processing your payment. Please try again.</p></div>} />
        </Routes>
      </Router>
    </AntApp>
  );
}

export default App;
